<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card border-0 shadow-sm">

          <!-- HEADER -->
          <div class="card-header cap-header">
            <h6 class="text-white mb-0">
              {{ $t("authorizationRequest.title") }}
            </h6>
          </div>

          <div class="card-body px-4 pb-4">

            <!-- INFO BAR -->
            <div class="info-strip mb-3">
              <i class="fas fa-info-circle me-2"></i>
              {{ $t("authorizationRequest.info") }}
            </div>

            <form @submit.prevent="submitRequest">
              <div class="row g-4">

                <!-- DATE -->
                <div class="col-md-6">
                  <label class="form-label">
                    {{ $t("authorizationRequest.form.date") }}
                  </label>
                  <div class="border rounded p-2 d-flex align-items-center">
                    <i class="far fa-calendar-alt me-2 text-muted"></i>
                    <input
                      type="date"
                      class="form-control border-0"
                      v-model="form.date"
                      :min="today"
                      required
                    />
                  </div>
                </div>

                <!-- START TIME -->
                <div class="col-md-3">
                  <label class="form-label">
                    {{ $t("authorizationRequest.form.startTime") }}
                  </label>
                  <div class="border rounded p-2 d-flex align-items-center">
                    <i class="far fa-clock me-2 text-muted"></i>
                    <input
                      type="time"
                      class="form-control border-0"
                      v-model="form.start_time"
                      required
                    />
                  </div>
                </div>

                <!-- END TIME -->
                <div class="col-md-3">
                  <label class="form-label">
                    {{ $t("authorizationRequest.form.endTime") }}
                  </label>
                  <div class="border rounded p-2 d-flex align-items-center">
                    <i class="far fa-clock me-2 text-muted"></i>
                    <input
                      type="time"
                      class="form-control border-0"
                      v-model="form.end_time"
                      required
                    />
                  </div>
                </div>

                <!-- DURATION -->
                <div class="col-md-6 d-flex align-items-end">
                  <div class="d-flex align-items-center gap-2">
                    <span class="text-secondary">
                      {{ $t("authorizationRequest.form.duration") }}:
                    </span>
                    <span
                      class="pill"
                      :class="isOverLimit ? 'pill-danger' : 'pill-ok'"
                      :title="isOverLimit
                        ? $t('authorizationRequest.warnings.overLimitTitle')
                        : $t('authorizationRequest.warnings.withinLimit')"
                    >
                      {{ numericDuration.toFixed(1) }}h
                    </span>
                  </div>
                </div>

                <!-- REASON -->
                <div class="col-12 mb-3">
                  <label class="form-label">
                    {{ $t("authorizationRequest.form.reason") }}
                  </label>

                  <div class="border rounded p-2">
                    <textarea
                      v-model="form.reason"
                      class="form-control border-0"
                      rows="4"
                      :placeholder="$t('authorizationRequest.form.reasonPlaceholder')"
                      style="resize: vertical;"
                    ></textarea>
                  </div>
                </div>

                <!-- WARNING & ACTIONS -->
                <div class="col-12">
                  <div
                    v-if="isOverLimit"
                    class="alert alert-warning d-flex align-items-start"
                  >
                    <i class="fas fa-exclamation-triangle me-2 mt-1"></i>
                    <div>
                      {{ $t("authorizationRequest.warnings.overLimit") }}
                      <div class="mt-2">
                        <button
                          type="button"
                          class="btn btn-outline-primary btn-sm"
                          @click="goToLeave"
                        >
                          {{ $t("authorizationRequest.buttons.goToLeave") }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      @click="resetForm"
                    >
                      {{ $t("authorizationRequest.buttons.reset") }}
                    </button>

                    <button
                      type="submit"
                      class="btn btn-cap"
                      :disabled="loading || !isValid || isOverLimit"
                      :title="isOverLimit
                        ? $t('authorizationRequest.warnings.overLimitTitle')
                        : ''"
                    >
                      <i
                        v-if="loading"
                        class="fas fa-spinner fa-spin me-1"
                      ></i>
                      <i
                        v-else
                        class="fas fa-paper-plane me-1"
                      ></i>
                      {{
                        loading
                          ? $t("authorizationRequest.buttons.submitting")
                          : $t("authorizationRequest.buttons.submit")
                      }}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <!-- RECENT REQUESTS -->
            <div class="mt-5">
              <h6 class="text-dark fw-bold mb-3">
                {{ $t("authorizationRequest.list.title") }}
              </h6>

              <div v-if="isLoading" class="text-muted">
                {{ $t("authorizationRequest.list.loading") }}
              </div>

              <div
                v-else-if="recentRequests.length === 0"
                class="text-muted"
              >
                {{ $t("authorizationRequest.list.empty") }}
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead class="bg-light">
                    <tr>
                      <th>{{ $t("authorizationRequest.list.table.date") }}</th>
                      <th>{{ $t("authorizationRequest.list.table.time") }}</th>
                      <th>{{ $t("authorizationRequest.list.table.duration") }}</th>
                      <th>{{ $t("authorizationRequest.list.table.status") }}</th>
                      <th>{{ $t("authorizationRequest.list.table.reason") }}</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="r in recentRequests" :key="r.id">
                      <td>{{ formatDate(r.date) }}</td>
                      <td>{{ r.start_time }} - {{ r.end_time }}</td>
                      <td>{{ Number(r.duration_hours).toFixed(1) }}h</td>
                      <td>
                        <span
                          class="badge rounded-pill"
                          :class="{
                            'bg-warning': r.status === 'Pending',
                            'bg-success': r.status === 'Approved',
                            'bg-danger': r.status === 'Rejected',
                            'bg-secondary': !['Pending','Approved','Rejected'].includes(r.status)
                          }"
                        >
                          {{ r.status }}
                        </span>
                      </td>
                      <td class="text-truncate" style="max-width: 320px">
                        {{ r.reason || '—' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useRouter } from 'vue-router'
import axios from '@/api'

const toast = useToast()
const router = useRouter()

// --- API & routing ---
const API_PREFIX = '/employee'                     // backend prefix
const LEAVE_ROUTE = '/employee/leaves/request'     // <-- route demandée

// --- User ---
const user = JSON.parse(localStorage.getItem('user') || '{}')
const userId = String(user?.id || '')

// --- State ---
const loading = ref(false)
const isLoading = ref(false)
const recentRequests = ref([])

const form = ref({
  user_id: userId,
  date: '',
  start_time: '',
  end_time: '',
  reason: ''
})

// --- Helpers / computed ---
const today = computed(() => new Date().toISOString().split('T')[0])

const numericDuration = computed(() => {
  if (!form.value.start_time || !form.value.end_time) return 0
  const start = new Date(`2000-01-01T${form.value.start_time}:00`)
  const end   = new Date(`2000-01-01T${form.value.end_time}:00`)
  if (end <= start) return 0
  return (end - start) / 36e5
})

const isOverLimit = computed(() => numericDuration.value > 2 - 1e-9) // > 2h

const isValid = computed(() => {
  if (!form.value.date || !form.value.start_time || !form.value.end_time) return false
  // end > start
  return numericDuration.value > 0
})

// --- API calls ---
async function fetchUserRequests () {
  if (!userId) return
  isLoading.value = true
  try {
    const { data } = await axios.get(`${API_PREFIX}/authorizations/user`)

    recentRequests.value = data.authorizations?.data || data.authorizations || []
  } catch (e) {
    toast.error('Failed to fetch your authorization requests')
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchUserRequests)

// --- Actions ---
function resetForm () {
  form.value = { user_id: userId, date: '', start_time: '', end_time: '', reason: '' }
}

function formatDate (d) {
  return d ? new Date(d).toLocaleDateString('en-GB') : 'N/A'
}

function goToLeave () {
  router.push(LEAVE_ROUTE)
}

function validateBusinessRules () {
  if (isOverLimit.value) {
    toast.warning('Duration is over 2h. Please create a Leave request instead.')
    return false
  }
  return true
}

async function submitRequest () {
  if (!userId) {
    toast.error('User ID missing. Please re-login.')
    return
  }
  if (!isValid.value) {
    toast.error('Please fill in date and valid times.')
    return
  }
  if (!validateBusinessRules()) return

  try {
    loading.value = true
    await axios.post(`${API_PREFIX}/authorizations`, form.value)
    toast.success('Authorization submitted successfully!')
    await fetchUserRequests()
    resetForm()
  } catch (e) {
    if (e?.response?.status === 422) {
      const messages = Object.values(e.response.data.errors || {}).flat().join(', ')
      toast.error(`Validation: ${messages}`)
    } else {
      toast.error('Submission failed')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>

.container-fluid { max-width: 1320px; }
.card { border-radius: 12px; overflow: hidden; }
.table th { font-size:.875rem; text-transform:uppercase; letter-spacing:.025em; }


.cap-header {
  background: linear-gradient(195deg, #b03a45, #8d2e38);
  padding: 1rem 1.25rem;
  border-radius: 12px 12px 0 0; 
  box-shadow: none;
}



.btn-cap {
  background-color: #b03a45;
  border-color: #b03a45;
  color: #fff;
}
.btn-cap:hover { background-color:#8d2e38; border-color:#8d2e38; color:#fff; }


.input-with-icon .input-group-text {
  background: #fff;
  border-right: 0;
}
.input-with-icon .form-control {
  border-left: 0;
}

/* New styles for bordered inputs */
.border .input-group-text {
  background: transparent;
  border: none;
}
.border .form-control {
  border: none;
}


.info-strip{
  background: #fff5f5;
  color: #b03a45;
  border: 1px dashed #e4a5ab;
  padding: .65rem .9rem;
  border-radius: 8px;
  font-weight: 500;
}


.pill{
  display:inline-block;
  padding:.25rem .6rem;
  border-radius:999px;
  font-weight:700;
  font-size:.9rem;
}
.pill-ok{ background:#e7f6ee; color:#1b7f46; }
.pill-danger{ background:#fdecec; color:#b63a3a; }


.page-title{ font-weight:700; }

.custom-border {
  border: 2px solid #ccc; /* Light gray border */
}

.custom-border:focus {
  border: 2px solid #ccc; /* Keep the border color on focus */
  box-shadow: none; /* Remove any shadow on focus */
}
</style>
